
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { Brain, MessageCircle, Clock, Search, Users, Zap, Globe, Activity, Sparkles } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

export const AiAssistanceSection = () => {
  const isMobile = useIsMobile();
  const navigatePath = isMobile ? "/ai-assistance-mobile" : "/ai-assistance";
  
  return (
    <div className="grid md:grid-cols-2 gap-12 items-center px-8 max-h-[470px] relative overflow-hidden">
      {/* Transparent background */}
      <div className="absolute inset-0 bg-transparent"></div>
      
      <div className="space-y-6 relative z-10">
        <h2 className="text-3xl font-bold">Assistant IA</h2>
        <p className="text-lg text-muted-foreground">
          Notre intelligence artificielle médicale vous accompagne 24h/24 pour répondre à vos questions de santé
        </p>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-primary" />
            <span>Consultations virtuelles à tout moment</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-primary" />
            <span>Analyse intelligente de vos symptômes</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-primary" />
            <span>Assistance personnalisée à votre profil</span>
          </div>
        </div>
        <Button asChild>
          <Link to={navigatePath}>Consulter l'assistant</Link>
        </Button>
      </div>
      
      {isMobile ? (
        // Enhanced mobile version with more engaging icons and animations
        <div className="relative z-10 animate-fade-in">
          <div className="space-y-3">
            {/* AI Brain Card */}
            <Card className="bg-gradient-to-br from-indigo-500 via-indigo-600 to-indigo-800 border-transparent shadow-lg transition-transform hover:scale-105 duration-300 overflow-hidden">
              <CardContent className="p-4 relative">
                <div className="flex items-center justify-between relative z-10">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 animate-fade-in" style={{animationDelay: "0.1s"}}>
                      <Brain className="h-6 w-6 text-white" />
                      <h4 className="font-bold text-white text-lg">
                        IA Médicale
                      </h4>
                    </div>
                    <p className="text-indigo-100 text-xs max-w-[80%] animate-fade-in" style={{animationDelay: "0.2s"}}>
                      Posez vos questions médicales et obtenez des réponses instantanées
                    </p>
                  </div>
                  
                  <div className="relative h-16 w-16 animate-fade-in" style={{animationDelay: "0.3s"}}>
                    <div className="absolute inset-0 bg-indigo-400/20 rounded-full animate-ping" style={{animationDuration: "3s"}}></div>
                    <div className="absolute inset-0 bg-indigo-400/30 rounded-full animate-pulse" style={{animationDuration: "2s"}}></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Sparkles className="h-8 w-8 text-white" />
                    </div>
                  </div>
                </div>
                
                {/* Brain wave animation in background */}
                <div className="absolute inset-0 opacity-10">
                  {Array.from({length: 5}).map((_, i) => (
                    <div 
                      key={i}
                      className="absolute h-0.5 bg-white rounded-full left-0 right-0"
                      style={{
                        top: `${20 + i * 15}%`,
                        animation: `pulse ${1.5 + i * 0.3}s ease-in-out infinite`,
                        animationDelay: `${i * 0.2}s`
                      }}
                    />
                  ))}
                </div>
                
                <div className="flex justify-between mt-4 relative z-10">
                  <div className="flex gap-6">
                    <div className="flex items-center gap-1">
                      <Zap className="h-4 w-4 text-indigo-200" />
                      <span className="text-xs text-indigo-100">Instantané</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Globe className="h-4 w-4 text-indigo-200" />
                      <span className="text-xs text-indigo-100">Multilingue</span>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => window.location.href = navigatePath}
                    className="bg-white/10 text-white hover:bg-white hover:text-indigo-600 backdrop-blur-sm rounded-full px-4"
                  >
                    <MessageCircle className="h-3.5 w-3.5 mr-1" />
                    Consulter
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {/* AI Stats Cards */}
            <div className="grid grid-cols-3 gap-3 animate-fade-in" style={{animationDelay: "0.4s"}}>
              <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-transparent shadow-sm">
                <CardContent className="p-3 flex flex-col items-center justify-center">
                  <div className="bg-indigo-500 text-white p-1.5 rounded-full mb-1">
                    <Clock className="h-4 w-4" />
                  </div>
                  <span className="text-lg font-bold text-indigo-700">24/7</span>
                  <span className="text-xs text-indigo-600">Disponible</span>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-transparent shadow-sm">
                <CardContent className="p-3 flex flex-col items-center justify-center">
                  <div className="bg-indigo-500 text-white p-1.5 rounded-full mb-1">
                    <Activity className="h-4 w-4" />
                  </div>
                  <span className="text-lg font-bold text-indigo-700">96%</span>
                  <span className="text-xs text-indigo-600">Précision</span>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-transparent shadow-sm">
                <CardContent className="p-3 flex flex-col items-center justify-center">
                  <div className="bg-indigo-500 text-white p-1.5 rounded-full mb-1">
                    <Users className="h-4 w-4" />
                  </div>
                  <span className="text-lg font-bold text-indigo-700">10k+</span>
                  <span className="text-xs text-indigo-600">Utilisateurs</span>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      ) : (
        // Desktop version with animated brain visualization instead of static image
        <div className="relative z-10 flex justify-center items-center">
          <div className="w-full h-[320px] bg-gradient-to-br from-indigo-50 via-purple-50 to-indigo-100 rounded-xl shadow-lg p-6 relative overflow-hidden">
            
            {/* Brain Visualization */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <div className="relative">
                <div className="absolute inset-0 bg-indigo-300/20 rounded-full h-32 w-32 animate-ping" style={{animationDuration: "3s"}}></div>
                <div className="absolute inset-0 bg-indigo-300/30 rounded-full h-32 w-32 animate-pulse" style={{animationDuration: "2s"}}></div>
                <div className="relative bg-gradient-to-br from-indigo-400 to-purple-500 h-32 w-32 rounded-full flex items-center justify-center shadow-lg">
                  <Brain className="h-16 w-16 text-white" />
                </div>
              </div>
            </div>
            
            {/* Neural Network Animation */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-64 w-64 rounded-full border-4 border-dashed border-indigo-200 animate-spin" style={{animationDuration: "20s"}}></div>
            </div>
            
            {/* Brain wave animations */}
            {Array.from({length: 5}).map((_, i) => (
              <div 
                key={i}
                className="absolute h-0.5 bg-indigo-400/30 rounded-full left-[20%] right-[20%]"
                style={{
                  top: `${30 + i * 8}%`,
                  animation: `pulse ${1.5 + i * 0.3}s ease-in-out infinite`,
                  animationDelay: `${i * 0.2}s`
                }}
              />
            ))}
            
            {/* Feature Cards */}
            <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-sm max-w-[180px] animate-fade-in" style={{animationDelay: "0.2s"}}>
              <div className="flex items-center gap-2 mb-2">
                <MessageCircle className="h-5 w-5 text-indigo-600" />
                <h4 className="font-semibold text-sm text-indigo-900">Consultations IA</h4>
              </div>
              <p className="text-xs text-gray-600">
                Posez vos questions et obtenez des réponses médicales précises
              </p>
            </div>
            
            <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-sm max-w-[180px] animate-fade-in" style={{animationDelay: "0.3s"}}>
              <div className="flex items-center gap-2 mb-2">
                <Search className="h-5 w-5 text-indigo-600" />
                <h4 className="font-semibold text-sm text-indigo-900">Analyse</h4>
              </div>
              <p className="text-xs text-gray-600">
                Analyse intelligente de vos symptômes et conseils personnalisés
              </p>
            </div>
            
            <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-sm max-w-[180px] animate-fade-in" style={{animationDelay: "0.4s"}}>
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-5 w-5 text-indigo-600" />
                <h4 className="font-semibold text-sm text-indigo-900">24/7</h4>
              </div>
              <p className="text-xs text-gray-600">
                Assistance médicale disponible à tout moment, où que vous soyez
              </p>
            </div>
            
            <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-sm max-w-[180px] animate-fade-in" style={{animationDelay: "0.5s"}}>
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-5 w-5 text-indigo-600" />
                <h4 className="font-semibold text-sm text-indigo-900">Pour tous</h4>
              </div>
              <p className="text-xs text-gray-600">
                Adapté à tous les profils, familles et professionnels de santé
              </p>
            </div>
            
            {/* Floating Particles */}
            {Array.from({length: 20}).map((_, i) => (
              <div 
                key={i}
                className="absolute rounded-full bg-indigo-300/30"
                style={{
                  width: `${3 + Math.random() * 5}px`,
                  height: `${3 + Math.random() * 5}px`,
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
                  animationDelay: `${Math.random() * 2}s`
                }}
              />
            ))}
            
            {/* Animation keyframes */}
            <style>
              {`
                @keyframes pulse {
                  0%, 100% {
                    opacity: 0.3;
                    transform: scaleX(0.8);
                  }
                  50% {
                    opacity: 0.6;
                    transform: scaleX(1);
                  }
                }
                
                @keyframes float {
                  0%, 100% {
                    transform: translateY(0);
                  }
                  50% {
                    transform: translateY(-10px);
                  }
                }
                
                @keyframes fade-in {
                  from {
                    opacity: 0;
                    transform: translateY(10px);
                  }
                  to {
                    opacity: 1;
                    transform: translateY(0);
                  }
                }
                
                .animate-fade-in {
                  animation: fade-in 0.5s ease-out forwards;
                }
              `}
            </style>
          </div>
        </div>
      )}
    </div>
  );
};
